import { useEffect, useState } from "react";
import { ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import { api, startCheckout } from "@/lib/api";

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [buying, setBuying] = useState(null);
  const [sizes, setSizes] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const buy = async (p) => {
    if (p.sizes?.length && !sizes[p.id]) {
      toast.info("Pick a size first.");
      return;
    }
    setBuying(p.id);
    try {
      await startCheckout(p.lookup_key, 1, sizes[p.id]);
    } catch {
      toast.error("Checkout failed — try again.");
      setBuying(null);
    }
  };

  return (
    <main data-testid="merch-page" className="pb-40 pt-36">
      <div className="mx-auto max-w-7xl px-6">
        <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">THE SHOP</p>
        <h1 data-testid="merch-title" className="font-anton leading-[0.9]">
          <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>WEAR THE</MaskedLine>
          <MaskedLine className="text-outline text-6xl md:text-8xl" delay={0.25}>SMOKE</MaskedLine>
        </h1>
        <p className="mt-8 max-w-lg text-white/60">
          Limited runs only. When a drop sells out, it's gone — no restocks, no reprints.
        </p>
      </div>

      <EditorialMarquee className="mt-16" speed={50} items={["LIMITED DROP", "NO RESTOCKS", "SOUTHERN SMOKE", "MILLZZ"]} />

      <div className="mx-auto mt-20 grid max-w-7xl gap-6 px-6 md:grid-cols-2">
        {products.map((p, i) => (
          <Reveal key={p.id} delay={(i % 2) * 0.12} className={i === 0 ? "md:row-span-2" : ""}>
            <div
              data-testid={`merch-card-${p.id}`}
              className="group flex h-full flex-col border border-white/10 bg-card"
            >
              <div className={`relative overflow-hidden ${i === 0 ? "aspect-[4/5]" : "aspect-[16/9]"}`}>
                <img
                  src={p.image}
                  alt={p.name}
                  className="h-full w-full object-cover transition-[transform,filter] duration-700 group-hover:scale-105"
                  style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 40px), calc(100% - 40px) 100%, 0 100%)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="absolute left-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary backdrop-blur">
                  {p.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-4 border-t border-white/10 p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-oswald text-xl font-bold tracking-wider text-white">{p.name}</p>
                    <p data-testid={`merch-price-${p.id}`} className="mt-1 font-mono text-sm text-primary">
                      ${p.price.toFixed(2)}
                    </p>
                  </div>
                  {p.sizes?.length > 0 && !p.sizes.some((s) => s.stock > 0) ? (
                    <span data-testid={`merch-soldout-${p.id}`} className="border border-white/20 px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-white/40">
                      SOLD OUT
                    </span>
                  ) : (
                    <button
                      data-testid={`merch-buy-${p.id}`}
                      onClick={() => buy(p)}
                      disabled={buying === p.id}
                      className="flex items-center gap-2 bg-primary px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[4px_4px_0px_#fff] disabled:opacity-50"
                    >
                      <ShoppingCart size={14} />
                      {buying === p.id ? "..." : "BUY"}
                    </button>
                  )}
                </div>
                {p.sizes?.length > 0 && p.sizes.some((s) => s.stock > 0) && (
                  <div data-testid={`merch-sizes-${p.id}`} className="flex flex-wrap gap-2">
                    {p.sizes.map((s) => (
                      <button
                        key={s.size}
                        data-testid={`merch-size-${p.id}-${s.size.toLowerCase()}`}
                        disabled={s.stock <= 0}
                        onClick={() => setSizes({ ...sizes, [p.id]: s.size })}
                        className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                          s.stock <= 0
                            ? "cursor-not-allowed border-white/10 text-white/25 line-through"
                            : sizes[p.id] === s.size
                              ? "border-primary bg-primary font-bold text-black"
                              : "border-white/25 text-white/70 hover:border-primary hover:text-primary"
                        }`}
                      >
                        {s.size}{s.stock > 0 && s.stock <= 3 ? ` · ${s.stock} LEFT` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <p data-testid="merch-secure-note" className="mx-auto mt-12 max-w-7xl px-6 font-mono text-[10px] tracking-[0.25em] text-white/30">
        SECURE CHECKOUT POWERED BY STRIPE — CARDS, APPLE PAY, GOOGLE PAY.
      </p>
    </main>
  );
}
